import { Subject } from 'rxjs';
import { AbstractControl, FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import {
  SdButtonComponent,
  SdFormErrorComponent,
  SdFormInputComponent,
  SdModalDefaultComponent,
  StatusEnum
} from '@seb/web-design-system/core';
import { InputTypeEnum } from '../../../enums';
import {
  BaseStoreService,
  DqeService,
  ProductNotificationService,
  ToasterService,
  TrackingService
} from '../../../services';
import { CustomPatternValidator } from '../../../constants';
import { isEmailValid, isValidEmailFormat } from '../../../utils';

@Component({
  selector: 'wc-product-notification-modal',
  templateUrl: './product-notification-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdModalDefaultComponent,
    FormsModule,
    ReactiveFormsModule,
    SdFormInputComponent,
    SdFormErrorComponent,
    SdButtonComponent,
    TranslatePipe
  ]
})
export class ProductNotificationModalComponent implements OnInit, OnDestroy {
  @Input()
  productCode = '';

  @Input()
  productName = '';

  @Input()
  notifyModalOpened = false;

  @Output()
  notifyModalOpenedChange = new EventEmitter<boolean>();

  notifyForm: FormGroup;
  useDataQualityFormValidation = false;
  suggestion: any;
  typingTimer: any;
  private readonly destroy$: Subject<void> = new Subject();

  constructor(
    private productNotificationService: ProductNotificationService,
    private formBuilder: FormBuilder,
    private toasterService: ToasterService,
    private trackingService: TrackingService,
    private baseStoreService: BaseStoreService,
    private dqeService: DqeService,
    private cdRef: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
  }

  ngOnInit(): void {
    const baseStore = this.baseStoreService.baseStore;
    this.useDataQualityFormValidation = baseStore.enableDQE;
    this.notifyForm = this.formBuilder.group({
      email: [
        '',
        this.useDataQualityFormValidation
          ? [Validators.required]
          : [Validators.pattern(CustomPatternValidator.email), Validators.minLength(4), Validators.required]
      ]
    });
  }

  cancelNotify(): void {
    this.notifyModalOpenedChange.emit(false);
    this.notifyModalOpened = false;
  }

  onModalClose(isOpened: boolean): void {
    if (!isOpened) {
      this.notifyModalOpenedChange.emit(false);
      this.notifyModalOpened = false;
    }
  }

  submitEmail(): void {
    const emailValue = this.notifyForm.get('email').value;

    if (this.notifyForm.valid) {
      this.productNotificationService.notifyEmail(emailValue, this.productCode).subscribe(
        (_response) => {
          this.toasterService.sendToasterEvent({
            status: StatusEnum.success,
            title: 'notify.notifyToastTitle',
            description: 'notify.notifyToastDescription'
          });
          this.trackingService.sendEvent('notifyMeClick', { productName: this.productName });
        },
        (_error) => {
          this.toasterService.sendToasterEvent({
            status: StatusEnum.error,
            title: 'notify.notifyErrorToastTitle',
            description: 'notify.notifyErrorToastDescription'
          });
        }
      );

      this.notifyModalOpenedChange.emit(false);

      this.notifyForm.patchValue({ email: '' });
    } else {
      this.notifyForm.markAllAsTouched();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadSuggestions(value: string) {
    this.onInputChange(this.notifyForm.get('email'));
    if (isPlatformBrowser(this.platformId)) {
      this.typingTimer = setTimeout(() => {
        this.typingTimer = undefined;
        if (value.includes('@')) {
          this.dqeService.suggest(this.notifyForm.get('email')?.value).subscribe((result) => {
            this.suggestion = result['suggest'].map((key) => {
              return {
                value: key
              };
            });
            this.cdRef.markForCheck();
          });
        }
      }, 600);
    }
  }

  validateInput() {
    if (isValidEmailFormat(this.notifyForm.get(InputTypeEnum.EMAIL)?.value)) {
      this.dqeService
        .validate(this.notifyForm.get(InputTypeEnum.EMAIL)?.value, InputTypeEnum.EMAIL)
        .subscribe((isValid) => {
          if (!isEmailValid(isValid)) {
            this.notifyForm.get(InputTypeEnum.EMAIL).setErrors({
              pattern: true,
              idError:
                Object.values(isValid).find((obj) => obj.IdError)?.IdError === '92'
                  ? Object.values(isValid).find((obj) => obj.IdError)?.Redressement +
                    '_' +
                    Object.values(isValid).find((obj) => obj.IdError)?.IdError
                  : Object.values(isValid).find((obj) => obj.IdError)?.IdError,
              suggestedEmail:
                Object.values(isValid).find((obj) => obj.IdError)?.Redressement === 1
                  ? Object.values(isValid).find((obj) => obj.IdError)?.eMail
                  : ''
            });
            this.cdRef.markForCheck();
          } else {
            const errors = this.notifyForm.get(InputTypeEnum.EMAIL).errors;
            if (errors?.pattern) {
              delete errors.pattern;
              delete errors.idError;
              this.notifyForm.get(InputTypeEnum.EMAIL).setErrors(Object.keys(errors).length ? errors : null);
              this.notifyForm.get(InputTypeEnum.EMAIL).updateValueAndValidity();
              this.cdRef.markForCheck();
            }
            this.notifyForm.get(InputTypeEnum.EMAIL).updateValueAndValidity();
            this.cdRef.markForCheck();
          }
        });
    }
  }

  onInputChange(input: AbstractControl) {
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
    }
    if (input?.value?.length > 0) {
      input?.markAsPending();
    }
  }

  userStoppedTyping() {
    this.validateInput();
  }
}
